/**
 * Header cloned from the marketing site's Navigation (ClickUp 86banyvn7) so
 * customer-facing pages (/account) carry the exact same chrome as
 * legacyorb.com. Marketing pages live on another origin, so those nav links
 * are plain absolute <a>s (never "active" here); "My Account" and the CTA
 * are in-app routes. Keep markup/classes in sync with
 * apps/marketing/src/components/navigation.tsx.
 */
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { MARKETING_SITE_URL } from '@/lib/funnel';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/products', label: 'Products' },
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact' },
];

const inactiveLinkClass =
  'text-sm font-medium transition-colors hover:text-foreground border-b-2 pb-0.5 text-muted-foreground border-transparent hover:border-foreground';
const activeLinkClass =
  'text-sm font-medium transition-colors hover:text-foreground border-b-2 pb-0.5 text-foreground border-foreground';

export function MarketingHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const accountActive = pathname === '/account';

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 md:h-20 items-center justify-between">
        <a href={MARKETING_SITE_URL} className="flex items-center" aria-label="LegacyOrb">
          {/* Mobile: logo only */}
          <img
            src="/logos/Logo_transparent.png"
            alt="LegacyOrb"
            width={150}
            height={128}
            className="h-10 w-auto md:hidden"
          />
          {/* Desktop: logo with text */}
          <img
            src="/logos/Logo_right_text_Transparent.png"
            alt="LegacyOrb"
            width={480}
            height={150}
            className="hidden md:block h-12 w-auto"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={`${MARKETING_SITE_URL}${link.href === '/' ? '' : link.href}`}
              className={inactiveLinkClass}
            >
              {link.label}
            </a>
          ))}
          <Link to="/account" className={accountActive ? activeLinkClass : inactiveLinkClass}>
            My Account
          </Link>
          <Link
            to="/start"
            className="inline-flex h-9 items-center justify-center rounded-md bg-orb-gold px-5 text-sm font-medium text-white transition-colors hover:bg-orb-gold/90"
          >
            Start your Legacy
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="md:hidden border-t bg-background">
          <nav className="container flex flex-col gap-4 py-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={`${MARKETING_SITE_URL}${link.href === '/' ? '' : link.href}`}
                className={`${inactiveLinkClass} w-fit`}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/account"
              className={`${accountActive ? activeLinkClass : inactiveLinkClass} w-fit`}
              onClick={() => setMobileOpen(false)}
            >
              My Account
            </Link>
            <Link
              to="/start"
              className="inline-flex h-9 items-center justify-center rounded-md bg-orb-gold px-5 text-sm font-medium text-white transition-colors hover:bg-orb-gold/90 w-fit"
              onClick={() => setMobileOpen(false)}
            >
              Start your Legacy
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
